import React, { useState } from 'react';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { useTeams } from '@snipit/core/hooks/useTeams';
import { useAuth } from '@snipit/core/context/AuthContext';
import { Shield, Users, Inbox, Building, Settings as SettingsIcon, Settings, Check, X, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ORG_ROLES = [
  'Administrator',
  'Transformation Engineer',
  'Migration Engineer',
  'AI Engineer',
  'Cloud Engineer',
  'Change Manager',
  'Sales Engineer',
  'IT Security',
  'Project Manager'
];

export default function OrgAdminView() {
  const { organization, orgMember, members, requests, loading: orgLoading, createOrganization, approveRequest, rejectRequest, checkDomainAndRequestAccess, transferOwnership, exportOrganizationData, deleteOrganization, inviteMember, updateMemberRole, bulkImportMembers } = useOrganization();
  const { teams, createTeam, addUserToTeam, removeUserFromTeam, deleteTeam, setTeamRole } = useTeams();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDomain, setNewOrgDomain] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const { t } = useTranslation();

  if (orgLoading) {
    return <div className="p-8 text-white">{t('Loading Organization Data...')}</div>;
  }

  // If user is not in an organization
  if (!organization) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-[#121212]">
        <Building size={64} className="text-indigo-500 mb-4" />
        <h2 className="text-3xl font-bold text-white mb-2">{t('Enterprise Organization')}</h2>
        <p className="text-gray-400 max-w-lg mb-8">
          {t('Manage your team, centralize billing, and securely share snippets across your entire company.')}
        </p>
        
        <div className="bg-[#1a1a1a] border border-gray-800 p-6 rounded-xl w-full max-w-md">
          <h3 className="text-xl font-bold text-white mb-4">{t('Create New Organization')}</h3>
          <div className="space-y-4 mb-8">
            <input 
              type="text" 
              placeholder={t('Organization Name (e.g. Acme Corp)')} 
              className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-indigo-500 outline-none"
              value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)}
            />
            <input 
              type="text" 
              placeholder={t('Domain (optional, e.g. acme.com)')} 
              className="w-full bg-[#121212] border border-gray-800 rounded p-3 text-white focus:border-indigo-500 outline-none"
              value={newOrgDomain}
              onChange={e => setNewOrgDomain(e.target.value)}
            />
            <button 
              onClick={() => createOrganization(newOrgName, newOrgDomain)}
              disabled={!newOrgName.trim()}
              className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors"
            >
              {t('Create Organization')}
            </button>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <h3 className="text-lg font-bold text-white mb-2">{t('Join Existing Organization')}</h3>
            <p className="text-xs text-gray-500 mb-4">{t('If your company already uses Snip-IT Enterprise, you can request access to join their organization.')}</p>
            <button 
              onClick={async () => {
                const orgName = await checkDomainAndRequestAccess();
                if (orgName) {
                  alert(`Access requested to ${orgName}. Please wait for your administrator to approve.`);
                } else {
                  alert("No organization found matching your email domain. Please ask your administrator for an invite.");
                }
              }}
              className="w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg transition-colors border border-gray-700"
            >
              {t('Check Domain & Request Access')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isAdmin = orgMember?.role === 'admin' || orgMember?.role === 'Administrator';

  // If user is in an org but NOT an admin
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-[#121212]">
        <Shield size={64} className="text-gray-600 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">{t('Access Denied')}</h2>
        <p className="text-gray-400 max-w-md">{t('You are a member of ')} <b>{organization.name}</b>, {t('but you do not have administrative privileges to view this page.')}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#121212] text-gray-300 flex flex-col p-6 lg:p-8 overflow-y-auto">
      <div className="flex items-center justify-between mb-8 shrink-0">
        <div className="flex items-center gap-3">
          <Building className="text-indigo-500" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">{organization.name}</h1>
            <p className="text-xs text-gray-500">{t('Organization Settings & Management')}</p>
          </div>
        </div>
        <div className="bg-indigo-900/30 text-indigo-400 border border-indigo-900/50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
          {organization.licenseTier}
        </div>
      </div>

      <div className="flex gap-4 mb-6 border-b border-gray-800 pb-2 shrink-0 overflow-x-auto">
        <button
          onClick={() => setActiveTab('members')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'members' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Users size={16} /> {t('Members')}
          {activeTab === 'members' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5g-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'requests' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Inbox size={16} /> {t('Access Requests')}
          {requests.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{requests.length}</span>}
          {activeTab === 'requests' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('teams')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'teams' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Shield size={16} /> {t('Teams')}
          {activeTab === 'teams' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'settings' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Settings size={16} /> {t('Settings')}
          {activeTab === 'settings' && (
            <span className="absolute -bottom-2.25left-0 right-0 h-0.5g-indigo-500 rounded-t-full" />
          )}
        </button>
      </div>

      <div className="grow">
        {activeTab === 'members' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">{t('Organization Members')}</h2>
              {isAdmin && (
                <div className="flex gap-2">
                  <div className="flex bg-[#1a1a1a] border border-gray-800 rounded">
                    <input 
                      type="email" 
                      placeholder={t('Email address...')} 
                      className="bg-transparent text-sm text-white px-3 py-1.5 outline-none w-48"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                    />
                    <button 
                      onClick={async () => {
                        if (!inviteEmail) return;
                        try {
                          await inviteMember(inviteEmail);
                          setInviteEmail('');
                          alert(t('Invite sent!'));
                        } catch (err) {
                          alert(err.message);
                        }
                      }}
                      className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 text-sm font-bold transition-colors"
                    >
                      {t('Invite')}
                    </button>
                  </div>
                  <label className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 text-sm font-bold transition-colors cursor-pointer rounded flex items-center border border-gray-700">
                    {t('Upload CSV')}
                    <input 
                      type="file" 
                      accept=".csv"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const text = await file.text();
                        // Parse based on provided CSV structure:
                        // 0: Employee ID, 1: First Name, 2: Last Name, 3: Email Address, 4: Employee Type, 5: Employee Title, 6: Manager Email, 7: Department
                        const rows = text.split('\n').map(r => r.trim()).filter(r => r);
                        // Skip header if it exists
                        const startIndex = rows[0].toLowerCase().includes('email') || rows[0].toLowerCase().includes('employee id') ? 1 : 0;
                        const csvData = [];
                        
                        // Simple CSV parser that handles basic quotes
                        const parseCsvRow = (row) => {
                          const result = [];
                          let insideQuotes = false;
                          let current = '';
                          for (let i = 0; i < row.length; i++) {
                            const char = row.charAt(i);
                            if (char === '"' && row.charAt(i+1) === '"') {
                              current += '"';
                              i++;
                            } else if (char === '"') {
                              insideQuotes = !insideQuotes;
                            } else if (char === ',' && !insideQuotes) {
                              result.push(current);
                              current = '';
                            } else {
                              current += char;
                            }
                          }
                          result.push(current);
                          return result.map(s => s.trim());
                        };

                        for (let i = startIndex; i < rows.length; i++) {
                          const cols = parseCsvRow(rows.at(i));
                          if (cols.length >= 4) { // At least up to email
                            const firstName = cols[1] || '';
                            const lastName = cols[2] || '';
                            csvData.push({
                              userName: `${firstName} ${lastName}`.trim(),
                              userEmail: cols[3],
                              role: cols[5] || 'Member', // Employee Title
                              teamName: cols[7] || '' // Department
                            });
                          }
                        }
                        if (csvData.length > 0) {
                          try {
                            await bulkImportMembers(csvData);
                            alert(`Imported ${csvData.length} members`);
                          } catch (err) {
                            alert(err.message);
                          }
                        }
                        e.target.value = ''; // Reset
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
            <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-left text-sm text-gray-300">
                <thead className="bg-[#222] text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3">{t('User')}</th>
                    <th className="px-4 py-3">{t('Role')}</th>
                    <th className="px-4 py-3">{t('Joined')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {members.map(m => (
                    <tr key={m.id} className="hover:bg-[#222] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-white">{m.userName}</span>
                          <span className="text-xs text-gray-500">{m.userEmail}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isAdmin && organization.ownerId !== m.userId && m.userId !== currentUser.uid ? (
                            <select 
                              className={`px-2 py-0.5 rounded text-[10px] font-bold outline-none cursor-pointer ${(m.role === 'admin' || m.role === 'Administrator') ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-500/30' : 'bg-gray-800 text-gray-300 border border-gray-700'}`}
                              value={m.role}
                              onChange={(e) => updateMemberRole(m.id, e.target.value)}
                            >
                              {ORG_ROLES.map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${(m.role === 'admin' || m.role === 'Administrator') ? 'bg-indigo-900/40 text-indigo-400' : 'bg-gray-800 text-gray-300'}`}>
                              {m.role} {organization.ownerId === m.userId && '(Owner)'}
                            </span>
                          )}
                          {organization.ownerId === currentUser.uid && m.userId !== currentUser.uid && (m.role === 'admin' || m.role === 'Administrator') && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to transfer ownership to ${m.userName}? You will remain an admin but will no longer be the owner.`)) {
                                  transferOwnership(m.userId);
                                }
                              }}
                              className="text-[10px] bg-red-900/30 text-red-400 hover:bg-red-900/50 px-2 py-0.5 rounded transition-colors ml-2"
                            >
                              {t('Transfer Ownership')}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(m.joinedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">{t('Pending Requests')}</h2>
            {requests.length === 0 ? (
              <p className="text-gray-500 italic">{t('No pending access requests.')}</p>
            ) : (
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                {requests.map(req => (
                  <div key={req.id} className="bg-[#1a1a1a] border border-gray-800 p-4 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="font-bold text-white text-sm">{req.userName}</p>
                      <p className="text-xs text-gray-400">{req.userEmail}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{t('Requested:')} {new Date(req.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => approveRequest(req.id, req.orgId, req.userId, req.userEmail, req.userName)}
                        className="bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50 p-2 rounded transition-colors"
                        title={t('Approve')}
                      >
                        <Check size={16} />
                      </button>
                      <button 
                        onClick={() => rejectRequest(req.id)}
                        className="bg-red-900/30 text-red-400 hover:bg-red-900/50 p-2 rounded transition-colors"
                        title={t('Reject')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div className="space-y-6">
            <div className="bg-[#1a1a1a] border border-gray-800 p-6 rounded-xl flex items-end gap-4">
              <div className="grow">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">{t('Create New Team')}</label>
                <input 
                  type="text" 
                  placeholder={t('Team Name (e.g. Frontend)')} 
                  className="w-full bg-[#121212] border border-gray-800 rounded p-2.5 text-white focus:border-indigo-500 outline-none text-sm"
                  value={newTeamName}
                  onChange={e => setNewTeamName(e.target.value)}
                />
              </div>
              <button 
                onClick={() => {
                  createTeam(newTeamName);
                  setNewTeamName('');
                }}
                disabled={!newTeamName.trim()}
                className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded transition-colors flex items-center gap-2 text-sm"
              >
                <Plus size={16} /> {t('Create Team')}
              </button>
            </div>

            <div className="grid gap-4 grid-cols-1 xl:grid-cols-2">
              {teams.map(team => (
                <div key={team.id} className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-5 relative group">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-white text-lg">{team.name}</h3>
                    {orgMember?.role === 'admin' && (
                      <button 
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete the team "${team.name}"? This cannot be undone.`)) {
                            deleteTeam(team.id);
                          }
                        }}
                        className="text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1"
                        title={t('Delete Team')}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {members.map(member => {
                      const isInTeam = member.teamIds && member.teamIds.includes(team.id);
                      return (
                        <div key={member.id} className="flex items-center justify-between p-2 rounded hover:bg-[#222] transition-colors border border-transparent hover:border-gray-800">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-300">{member.userName}</span>
                            {isInTeam && member.teamRoles && Reflect.get(member.teamRoles, team.id) && Reflect.get(member.teamRoles, team.id) !== 'member' && Reflect.get(member.teamRoles, team.id) !== 'Member' && (
                              <span className="bg-purple-900/40 text-purple-400 px-1.5 py-0.5 rounded text-[10px] font-bold border border-purple-500/30">
                                {Reflect.get(member.teamRoles, team.id) === 'lead' ? t('Lead') : t(Reflect.get(member.teamRoles, team.id))}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isInTeam && isAdmin && (
                              <select
                                className="bg-gray-800 text-gray-300 text-[10px] px-2 py-1 rounded outline-none border border-gray-700 cursor-pointer"
                                value={Reflect.get(member.teamRoles || {}, team.id) || 'Member'}
                                onChange={(e) => setTeamRole(team.id, member.userId, e.target.value)}
                              >
                                <option value="lead">{t('Team Lead')}</option>
                                {ORG_ROLES.map(role => (
                                  <option key={role} value={role}>{role}</option>
                                ))}
                              </select>
                            )}
                            <button
                              onClick={() => isInTeam ? removeUserFromTeam(team.id, member.userId) : addUserToTeam(team.id, member.userId)}
                              className={`text-xs px-2 py-1 rounded font-bold transition-colors ${isInTeam ? 'text-red-400 hover:bg-red-900/30' : 'text-indigo-400 hover:bg-indigo-900/30'}`}
                            >
                              {isInTeam ? t('Remove') : t('Add')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6 max-w-2xl">
            <h2 className="text-xl font-bold text-white mb-4">{t('Organization Settings')}</h2>
            
            <div className="bg-[#1a1a1a] border border-gray-800 p-6 rounded-xl space-y-4">
              <h3 className="font-bold text-white">{t('Export Organization Data')}</h3>
              <p className="text-sm text-gray-400">
                {t('Download a JSON backup of all organization members, teams, and snippet data.')}
              </p>
              <button 
                onClick={async () => {
                  try {
                    await exportOrganizationData();
                  } catch (e) {
                    alert(e.message);
                  }
                }}
                className="bg-indigo-900/30 text-indigo-400 hover:bg-indigo-900/50 px-4 py-2 font-bold rounded transition-colors text-sm"
              >
                {t('Export Data')}
              </button>
            </div>

            {organization.ownerId === currentUser.uid && (
              <div className="bg-red-950/20 border border-red-900/50 p-6 rounded-xl space-y-4 mt-8">
                <h3 className="font-bold text-red-500">{t('Danger Zone: Delete Organization')}</h3>
                <p className="text-sm text-red-400/80">
                  {t('This action is completely irreversible. It will permanently delete the organization, all associated teams, members, and ALL snippets shared with this organization.')}
                </p>
                <button 
                  onClick={async () => {
                    const confirmText = prompt(`Are you absolutely sure? Type DELETE to permanently delete the organization "${organization.name}".`);
                    if (confirmText === 'DELETE') {
                      try {
                        await deleteOrganization();
                        alert('Organization deleted successfully.');
                        window.location.reload();
                      } catch (e) {
                        alert(e.message);
                      }
                    }
                  }}
                  className="bg-red-900 text-red-100 hover:bg-red-800 px-4 py-2 font-bold rounded transition-colors text-sm"
                >
                  {t('Delete Organization')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
